// @ts-nocheck
/*
 * Port of views/dashboard/notification/notification_list.js (671 lines).
 * BYTE-IDENTICAL â€” 4 assignment modals (assign-to-agent, remove-from-agent,
 * assign-to-user, remove-from-user), Promise.all delete on multi-select,
 * per-modal lazy load of agents/users, `isCallCentreTier` gate hides
 * user-assignment buttons for call-centre roles, `is_general` badge only
 * for superadmin, "Created By" column only for USER_ROLES.SUPERADMIN.
 * PrimeReact DataTable â†’ obd3-gui; Checkbox â†’ shadcn.
 */
import React, { useState, useEffect } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import Loader from '@/components/partials/components/Loader';
import RenderDeleteModal from '@/components/partials/components/delete-modal';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/data-table/DataTable';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_AGENTS_LIST,
  API_USERS_LIST,
  API_NOTIFICATION_ASSIGN_TO_AGENT,
  API_NOTIFICATION_LIST,
  API_NOTIFICATION_DELETE,
  API_NOTIFICATION_UNASSIGN_FROM_AGENT,
  API_NOTIFICATION_ASSIGN_TO_USER,
  API_NOTIFICATION_UNASSIGN_FROM_USER,
} from '@/service/url';
import { ROUTES, MODULE_ACL, USER_ROLES, USER_ROLES_MAP } from '@/utilities/constant';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { getUser } from '@/utilities/setting';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  List,
  Plus,
  Pencil,
  Trash2,
  UserPlus,
  UserMinus,
  Users,
  UsersRound,
  Search as SearchIcon,
  Megaphone,
} from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const EditIcon = ({ linkTo }) => (
  <Link
    to={linkTo}
    title="Edit"
    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
  >
    <Pencil className="h-3.5 w-3.5" />
  </Link>
);

const DeleteIcon = ({ handleCallback }) => (
  <button
    type="button"
    title="Delete"
    onClick={handleCallback}
    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
  >
    <Trash2 className="h-3.5 w-3.5" />
  </button>
);

const NotificationList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [dataList, setDataList] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isMultiDelete, setIsMultiDelete] = useState(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState({});
  const [tab, setTab] = usePersistedState('notification/NotificationList:tab', 'mine');
  const [tabLoading, setTabLoading] = useState(false);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [agentList, setAgentList] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [submittingAssign, setSubmittingAssign] = useState(false);
  const [submittingRemove, setSubmittingRemove] = useState(false);

  const [showUserAssignModal, setShowUserAssignModal] = useState(false);
  const [showUserRemoveModal, setShowUserRemoveModal] = useState(false);
  const [userList, setUserList] = useState([]);
  const [userListLoading, setUserListLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [submittingUserAssign, setSubmittingUserAssign] = useState(false);
  const [submittingUserRemove, setSubmittingUserRemove] = useState(false);

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    setUserRole(user?.role || null);
    const admin = user.role === USER_ROLES.SUPERADMIN || user.role === USER_ROLES.SUB_SUPERADMIN;
    if (admin) setIsSuperadmin(true);
    if (user?.acl_settings) setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    fetchData(admin ? tab : null);
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchData = (scope = isSuperAdmin ? tab : null) => {
    getApiCall(API_NOTIFICATION_LIST, scope ? { scope } : undefined)
      .then((resp) => {
        const data = resp?.data || resp;
        const updated = Array.isArray(data) ? data.map((val, idx) => ({ ...val, key: idx + 1 })) : [];
        setDataList(updated);
        setOriginalData(updated);
      })
      .catch((err) => {
        if (typeof err === 'string' && err.trim())
          toast.error(friendlyApiError(err) || 'Failed to fetch notifications');
        console.log('Error fetching notifications:', err);
      })
      .finally(() => {
        setLoading(false);
        setTabLoading(false);
      });
  };

  const switchTab = (next) => {
    if (next === tab) return;
    setTab(next);
    setSelectedItems([]);
    setTabLoading(true);
    fetchData(next);
  };

  useEffect(() => {
    if (searchValue.trim()) {
      const filtered = originalData.filter(
        (item) =>
          item.notification_type?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchValue.toLowerCase()),
      );
      setDataList(filtered);
    } else {
      setDataList(originalData);
    }
  }, [searchValue, originalData]);

  useEffect(() => {
    if (confirmDelete) {
      if (isMultiDelete && Array.isArray(selectedItems)) {
        const deletePromises = selectedItems.map((id) =>
          postApiCall(API_NOTIFICATION_DELETE, { id: String(id) }),
        );
        Promise.all(deletePromises)
          .then(() => {
            toast.success('Selected notifications deleted');
            fetchData();
            setSelectedItems([]);
          })
          .catch((err) => {
            toast.error(friendlyApiError(err) || 'Failed to delete selected notifications');
          })
          .finally(() => {
            setConfirmDelete(false);
            setEnableDeleteModal(false);
          });
      } else {
        postApiCall(API_NOTIFICATION_DELETE, { id: String(deleteId) })
          .then(() => {
            toast.success('Notification deleted successfully');
            fetchData();
          })
          .catch((err) => {
            toast.error(friendlyApiError(err) || 'Failed to delete notification');
          })
          .finally(() => {
            setConfirmDelete(false);
            setEnableDeleteModal(false);
          });
      }
    }
  }, [confirmDelete]);

  const canManageNotifications = isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.edit;

  const isCallCentreTier = [
    USER_ROLES.CALL_CENTRE_ADMIN,
    USER_ROLES.CALL_CENTRE_TEAM_LEAD,
    USER_ROLES.CALL_CENTRE_AGENT,
  ].includes(userRole);
  // Superadmin never assigns to agents, and assigns to users only from "My Notifications".
  const canAssignToAgents = canManageNotifications && !isSuperAdmin;
  const canAssignToUsers =
    canManageNotifications && !isCallCentreTier && (!isSuperAdmin || tab === 'mine');
  const noneSelected = selectedItems.length === 0;

  const handleSelectAll = (checked) => {
    setSelectedItems(checked ? dataList.map((item) => item.id) : []);
  };

  const loadAgents = async () => {
    if (agentLoading || agentList.length > 0) return;
    try {
      setAgentLoading(true);
      const resp = await getApiCall(API_AGENTS_LIST, { fields: 'min' });
      const normalized = Array.isArray(resp)
        ? resp.map((a) => ({
            id: a.id,
            agent_name: a.agent_name || a.username || `Agent #${a.id}`,
            username: a.username || '',
            agent_number: a.agent_number || '',
          }))
        : [];
      setAgentList(normalized);
    } catch (e) {
      toast.error(friendlyApiError(e) || 'Failed to load agents');
    } finally {
      setAgentLoading(false);
    }
  };

  const loadUsers = async () => {
    if (userListLoading || userList.length > 0) return;
    try {
      setUserListLoading(true);
      const resp = await getApiCall(API_USERS_LIST, { role: 'noRole' });
      const raw = resp?.data || resp;
      const normalized = Array.isArray(raw)
        ? raw.map((u) => ({
            id: u.id,
            name: u.name || u.username || `User #${u.id}`,
            username: u.username || '',
            email: u.email || '',
            role: u.role || '',
            roleLabel: USER_ROLES_MAP[u.role] || u.role || '',
          }))
        : [];
      setUserList(normalized);
    } catch (e) {
      toast.error(friendlyApiError(e) || 'Failed to load users');
    } finally {
      setUserListLoading(false);
    }
  };

  const openAssignModal = async () => {
    if (selectedItems.length === 0) return toast.warning('Select at least one notification');
    await loadAgents();
    setSelectedAgentIds([]);
    setAgentSearch('');
    setShowAssignModal(true);
  };

  const openRemoveModal = async () => {
    if (selectedItems.length === 0) return toast.warning('Select at least one notification');
    await loadAgents();
    setSelectedAgentIds([]);
    setAgentSearch('');
    setShowRemoveModal(true);
  };

  const openUserAssignModal = async () => {
    if (selectedItems.length === 0) return toast.warning('Select at least one notification');
    await loadUsers();
    setSelectedUserIds([]);
    setUserSearch('');
    setShowUserAssignModal(true);
  };

  const openUserRemoveModal = async () => {
    if (selectedItems.length === 0) return toast.warning('Select at least one notification');
    await loadUsers();
    setSelectedUserIds([]);
    setUserSearch('');
    setShowUserRemoveModal(true);
  };

  const submitAssignRemove = async (mode) => {
    const isAssign = mode === 'assign';
    if (selectedItems.length === 0) return toast.warning('Select at least one notification');
    if (selectedAgentIds.length === 0) return toast.warning('Select at least one agent');

    const payload = {
      notification_ids: selectedItems.map((id) => String(id)),
      agent_ids: selectedAgentIds.map((id) => String(id)),
    };

    try {
      isAssign ? setSubmittingAssign(true) : setSubmittingRemove(true);
      await postApiCall(
        isAssign ? API_NOTIFICATION_ASSIGN_TO_AGENT : API_NOTIFICATION_UNASSIGN_FROM_AGENT,
        payload,
      );
      toast.success(isAssign ? 'Assigned successfully' : 'Removed successfully');
      setShowAssignModal(false);
      setShowRemoveModal(false);
      setSelectedAgentIds([]);
    } catch (e) {
      toast.error(friendlyApiError(e) || 'Something went wrong');
    } finally {
      isAssign ? setSubmittingAssign(false) : setSubmittingRemove(false);
    }
  };

  const submitUserAssignRemove = async (mode) => {
    const isAssign = mode === 'assign';
    if (selectedItems.length === 0) return toast.warning('Select at least one notification');
    if (selectedUserIds.length === 0) return toast.warning('Select at least one user');

    const payload = {
      notification_ids: selectedItems.map((id) => String(id)),
      user_ids: selectedUserIds.map((id) => String(id)),
    };

    try {
      isAssign ? setSubmittingUserAssign(true) : setSubmittingUserRemove(true);
      await postApiCall(
        isAssign ? API_NOTIFICATION_ASSIGN_TO_USER : API_NOTIFICATION_UNASSIGN_FROM_USER,
        payload,
      );
      toast.success(isAssign ? 'Assigned to users' : 'Removed from users');
      setShowUserAssignModal(false);
      setShowUserRemoveModal(false);
      setSelectedUserIds([]);
    } catch (e) {
      toast.error(friendlyApiError(e) || 'Something went wrong');
    } finally {
      isAssign ? setSubmittingUserAssign(false) : setSubmittingUserRemove(false);
    }
  };

  const selectedNotificationsPreview = () => {
    const selectedSet = new Set(selectedItems);
    const items = originalData.filter((n) => selectedSet.has(n.id)).slice(0, 10);
    return (
      <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
        <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Megaphone className="h-3 w-3" />
          {selectedItems.length} notification{selectedItems.length === 1 ? '' : 's'} selected
        </div>
        {items.length > 0 && (
          <ul className="ml-4 list-disc space-y-0.5 text-xs text-foreground">
            {items.map((n) => (
              <li key={n.id}>
                <span className="font-semibold">{n.notification_type}</span>
                {n.description ? (
                  <span className="text-muted-foreground"> — {n.description}</span>
                ) : null}
              </li>
            ))}
            {selectedItems.length > 10 && (
              <li className="text-muted-foreground">…and more</li>
            )}
          </ul>
        )}
      </div>
    );
  };

  const filteredAgents = agentSearch.trim()
    ? agentList.filter((a) =>
        `${a.agent_name} ${a.username} ${a.agent_number}`.toLowerCase().includes(agentSearch.toLowerCase()),
      )
    : agentList;

  const toggleAgent = (id) => {
    setSelectedAgentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAllAgents = (checked) => {
    setSelectedAgentIds(checked ? filteredAgents.map((a) => a.id) : []);
  };

  const filteredUsers = userSearch.trim()
    ? userList.filter((u) =>
        `${u.name} ${u.username} ${u.email} ${u.roleLabel}`.toLowerCase().includes(userSearch.toLowerCase()),
      )
    : userList;

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAllUsers = (checked) => {
    setSelectedUserIds(checked ? filteredUsers.map((u) => u.id) : []);
  };

  if (loading) return <Loader />;

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      enableHiding: false,
      header: () => (
        <Checkbox
          checked={selectedItems.length === dataList.length && dataList.length > 0}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedItems.includes(row.original.id)}
          onCheckedChange={() =>
            setSelectedItems((prev) =>
              prev.includes(row.original.id) ? prev.filter((i) => i !== row.original.id) : [...prev, row.original.id],
            )
          }
          aria-label="Select row"
        />
      ),
    },
    { accessorKey: 'notification_type', header: 'Title' },
    { accessorKey: 'description', header: 'Description' },
  ];

  if (isSuperAdmin) {
    columns.push({
      accessorKey: 'is_general',
      header: 'Is General',
      cell: ({ row }) =>
        row.original.is_general ? (
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
            Yes
          </span>
        ) : (
          <span className="text-muted-foreground">No</span>
        ),
    });
  }

  if (userRole === USER_ROLES.SUPERADMIN) {
    columns.push({
      accessorKey: 'created_by_username',
      header: 'Created By',
      cell: ({ row }) => row.original.created_by_username || '-',
    });
  }

  columns.push({
    accessorKey: 'created_at',
    header: 'Created Date',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleString('en-IN'),
  });

  columns.push({
    id: 'actions',
    header: 'Action',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1.5">
        {(isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.edit) && (
          <EditIcon linkTo={`/notifications/edit/${row.original.id}`} />
        )}
        {(isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.delete) &&
          selectedItems.length === 0 && (
            <DeleteIcon
              handleCallback={() => {
                setDeleteId(row.original.id);
                setIsMultiDelete(false);
                setEnableDeleteModal(true);
              }}
            />
          )}
      </div>
    ),
  });

  return (
    <FillPage>
      <PageHeader
        title="Notifications"
        description="System notifications shown to agents / users, assignable per-recipient."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate(`/${ROUTES.NOTIFICATION.ASSIGNMENTS}`)}
            >
              <List className="h-4 w-4" />
              Assigned
            </Button>
            {(isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.add) && (
              <Button onClick={() => navigate(`/${ROUTES.NOTIFICATION.CREATE}`)}>
                <Plus className="h-4 w-4" />
                Create Notification
              </Button>
            )}
          </>
        }
      />
      {isSuperAdmin && (
        <Tabs value={tab} onValueChange={switchTab} className="mb-3">
          <TabsList>
            <TabsTrigger value="mine">My Notifications</TabsTrigger>
            <TabsTrigger value="all">All Notifications</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      <div
          className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/[0.06] px-3 py-2"
          style={{
            boxShadow:
              '4px 4px 10px rgba(163, 177, 198, 0.28), -4px -4px 10px rgba(255, 255, 255, 0.85)',
          }}
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
            <Megaphone className="h-3 w-3" />
            {selectedItems.length} selected
          </span>

          {(isSuperAdmin || loggedInUserACL[MODULE_ACL.NOTIFICATION]?.delete) && (
            <button
              type="button"
              disabled={noneSelected}
              onClick={() => {
                setIsMultiDelete(true);
                setEnableDeleteModal(true);
              }}
              className="disabled:pointer-events-none disabled:opacity-50 inline-flex items-center gap-1.5 rounded-md bg-rose-100 px-2.5 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          )}

          {canManageNotifications && (
            <>
              {canAssignToAgents && (
              <>
              <button
                type="button"
                disabled={noneSelected}
                onClick={openAssignModal}
                className="disabled:pointer-events-none disabled:opacity-50 inline-flex items-center gap-1.5 rounded-md bg-emerald-100 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 transition hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Assign agents
              </button>
              <button
                type="button"
                disabled={noneSelected}
                onClick={openRemoveModal}
                className="disabled:pointer-events-none disabled:opacity-50 inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2.5 py-1.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 transition hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-300"
              >
                <UserMinus className="h-3.5 w-3.5" />
                Remove agents
              </button>
              </>
              )}

              {canAssignToUsers && (
                <>
                  <button
                    type="button"
                    disabled={noneSelected}
                    onClick={openUserAssignModal}
                    className="disabled:pointer-events-none disabled:opacity-50 inline-flex items-center gap-1.5 rounded-md bg-sky-100 px-2.5 py-1.5 text-xs font-semibold text-sky-700 ring-1 ring-inset ring-sky-200 transition hover:bg-sky-200 dark:bg-sky-950/40 dark:text-sky-300"
                  >
                    <UsersRound className="h-3.5 w-3.5" />
                    Assign users
                  </button>
                  <button
                    type="button"
                    disabled={noneSelected}
                    onClick={openUserRemoveModal}
                    className="disabled:pointer-events-none disabled:opacity-50 inline-flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 text-xs font-semibold text-foreground ring-1 ring-inset ring-border/60 transition hover:bg-muted/70"
                  >
                    <Users className="h-3.5 w-3.5" />
                    Remove users
                  </button>
                </>
              )}
            </>
          )}

          <button
            type="button"
            disabled={noneSelected}
            onClick={() => setSelectedItems([])}
            className="disabled:pointer-events-none disabled:opacity-50 ml-auto text-[11px] font-semibold text-muted-foreground transition hover:text-foreground"
          >
            Clear
          </button>
        </div>

      <DataTable
        tableId="notification.list"
        columns={columns}
        data={dataList}
        isLoading={loading || tabLoading}
        emptyTitle="No notifications"
        pageSize={10}
        disableExport
      />
      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
        isMultiDelete={isMultiDelete}
      />

      {(showAssignModal || showRemoveModal) && (
        <Modal
          show={showAssignModal || showRemoveModal}
          onHide={() => {
            setShowAssignModal(false);
            setShowRemoveModal(false);
          }}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="border-b border-border/50 pb-3">
            <div className="flex items-center gap-3">
              <div
                className={
                  'flex h-10 w-10 items-center justify-center rounded-lg ring-1 ring-inset ' +
                  (showAssignModal
                    ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                    : 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300')
                }
              >
                {showAssignModal ? (
                  <UserPlus className="h-5 w-5" />
                ) : (
                  <UserMinus className="h-5 w-5" />
                )}
              </div>
              <div>
                <Modal.Title className="text-base font-bold text-foreground">
                  {showAssignModal
                    ? 'Assign notifications to agents'
                    : 'Remove notifications from agents'}
                </Modal.Title>
                <p className="text-[11px] text-muted-foreground">
                  Pick the agents this action applies to.
                </p>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            {selectedNotificationsPreview()}
            <div className="mt-3">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Search agents
              </label>
              <div className="relative mt-1">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  placeholder="Search by name / username / number"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  className="input-embossed h-9 w-full rounded-md pl-8 pr-3 text-xs"
                />
              </div>
            </div>

            <div className="mt-3 max-h-[320px] overflow-auto rounded-lg border border-border/60 bg-card p-2">
              <div className="mb-1 flex items-center gap-2 border-b border-border/40 px-1 pb-1">
                <Checkbox
                  checked={
                    filteredAgents.length > 0 &&
                    selectedAgentIds.length === filteredAgents.length
                  }
                  onCheckedChange={handleSelectAllAgents}
                />
                <span className="text-xs font-semibold text-foreground">
                  Select all
                </span>
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {selectedAgentIds.length} / {filteredAgents.length}
                </span>
              </div>
              {agentLoading ? (
                <div className="p-3 text-xs text-muted-foreground">
                  Loading agents…
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground">
                  No agents found
                </div>
              ) : (
                filteredAgents.map((a) => {
                  const active = selectedAgentIds.includes(a.id);
                  return (
                    <label
                      key={a.id}
                      className={
                        'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs transition ' +
                        (active
                          ? 'bg-primary/10 ring-1 ring-inset ring-primary/25'
                          : 'hover:bg-muted/50')
                      }
                    >
                      <Checkbox
                        checked={active}
                        onCheckedChange={() => toggleAgent(a.id)}
                      />
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-foreground">
                          {a.agent_name}
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {[a.username, a.agent_number].filter(Boolean).join(' • ')}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="border-t border-border/50 pt-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowAssignModal(false);
                setShowRemoveModal(false);
              }}
            >
              Cancel
            </Button>
            {showAssignModal ? (
              <Button
                onClick={() => submitAssignRemove('assign')}
                disabled={submittingAssign || agentLoading}
                className="gap-1.5"
              >
                <UserPlus className="h-4 w-4" />
                {submittingAssign ? 'Assigning…' : 'Assign'}
              </Button>
            ) : (
              <Button
                onClick={() => submitAssignRemove('remove')}
                disabled={submittingRemove || agentLoading}
                className="gap-1.5"
              >
                <UserMinus className="h-4 w-4" />
                {submittingRemove ? 'Removing…' : 'Remove'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {(showUserAssignModal || showUserRemoveModal) && (
        <Modal
          show={showUserAssignModal || showUserRemoveModal}
          onHide={() => {
            setShowUserAssignModal(false);
            setShowUserRemoveModal(false);
          }}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="border-b border-border/50 pb-3">
            <div className="flex items-center gap-3">
              <div
                className={
                  'flex h-10 w-10 items-center justify-center rounded-lg ring-1 ring-inset ' +
                  (showUserAssignModal
                    ? 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300'
                    : 'bg-muted text-foreground ring-border/60')
                }
              >
                {showUserAssignModal ? (
                  <UsersRound className="h-5 w-5" />
                ) : (
                  <Users className="h-5 w-5" />
                )}
              </div>
              <div>
                <Modal.Title className="text-base font-bold text-foreground">
                  {showUserAssignModal
                    ? 'Assign notifications to users'
                    : 'Remove notifications from users'}
                </Modal.Title>
                <p className="text-[11px] text-muted-foreground">
                  Pick the users this action applies to.
                </p>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            {selectedNotificationsPreview()}
            <div className="mt-3">
              <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Search users
              </label>
              <div className="relative mt-1">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  placeholder="Search by name / username / email / role"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="input-embossed h-9 w-full rounded-md pl-8 pr-3 text-xs"
                />
              </div>
            </div>

            <div className="mt-3 max-h-[320px] overflow-auto rounded-lg border border-border/60 bg-card p-2">
              <div className="mb-1 flex items-center gap-2 border-b border-border/40 px-1 pb-1">
                <Checkbox
                  checked={
                    filteredUsers.length > 0 &&
                    selectedUserIds.length === filteredUsers.length
                  }
                  onCheckedChange={handleSelectAllUsers}
                />
                <span className="text-xs font-semibold text-foreground">Select all</span>
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {selectedUserIds.length} / {filteredUsers.length}
                </span>
              </div>
              {userListLoading ? (
                <div className="p-3 text-xs text-muted-foreground">Loading users…</div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground">No users found</div>
              ) : (
                filteredUsers.map((u) => {
                  const active = selectedUserIds.includes(u.id);
                  return (
                    <label
                      key={u.id}
                      className={
                        'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs transition ' +
                        (active
                          ? 'bg-primary/10 ring-1 ring-inset ring-primary/25'
                          : 'hover:bg-muted/50')
                      }
                    >
                      <Checkbox
                        checked={active}
                        onCheckedChange={() => toggleUser(u.id)}
                      />
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-foreground">
                          {u.name}
                        </div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {[u.username, u.email, u.roleLabel].filter(Boolean).join(' • ')}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="border-t border-border/50 pt-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowUserAssignModal(false);
                setShowUserRemoveModal(false);
              }}
            >
              Cancel
            </Button>
            {showUserAssignModal ? (
              <Button
                onClick={() => submitUserAssignRemove('assign')}
                disabled={submittingUserAssign || userListLoading}
                className="gap-1.5"
              >
                <UsersRound className="h-4 w-4" />
                {submittingUserAssign ? 'Assigning…' : 'Assign'}
              </Button>
            ) : (
              <Button
                onClick={() => submitUserAssignRemove('remove')}
                disabled={submittingUserRemove || userListLoading}
                className="gap-1.5"
              >
                <Users className="h-4 w-4" />
                {submittingUserRemove ? 'Removing…' : 'Remove'}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}
    </FillPage>
  );
};

export default NotificationList;
